/*
 * Copyright 2025 ixPilot Teknoloji A.Ş.
 * All rights reserved.
 *
 * Hook to load bayi options from the backend.
 * Only accessible by admin users.
 */

import { useCallback, useEffect, useState } from "react";
import api from "utils/api";

const BAYI_ENDPOINT = "/api/bayi-yonetimi/bayiler";

const normalizeBayi = (bayi) => {
  if (!bayi || typeof bayi !== "object") return null;

  const id = bayi.id ?? null;
  const username = bayi.username ?? null;

  if (!id || !username) return null;

  return {
    id: String(id),
    name: username,
    raw: bayi,
  };
};

export const useBayiOptions = (enabled = true) => {
  const [bayiler, setBayiler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadBayiler = useCallback(() => {
    if (!enabled) {
      setLoading(false);
      setBayiler([]);
      return;
    }

    setLoading(true);
    setError(null);

    api
      .get(BAYI_ENDPOINT)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Bayi listesi yüklenemedi");
        }
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          const normalized = data
            .map(normalizeBayi)
            .filter(Boolean)
            .sort((a, b) => a.name.localeCompare(b.name, "tr"));
          setBayiler(normalized);
        } else {
          setBayiler([]);
        }
      })
      .catch((err) => {
        setError("Bayi listesi yüklenemedi. Lütfen tekrar deneyin.");
        setBayiler([]);
      })
      .finally(() => setLoading(false));
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      loadBayiler();
    } else {
      setLoading(false);
      setBayiler([]);
      setError(null);
    }
  }, [enabled, loadBayiler]);

  return {
    bayiler,
    loading,
    error,
    reload: loadBayiler,
  };
};

export default useBayiOptions;

